import styles from './ProductLogo.module.css';

interface GlobeMarkProps {
  size?: number;
  className?: string;
}

/** Tourism 360 globe mark — meridians on a sphere. */
function GlobeMark({ size, className = '' }: GlobeMarkProps) {
  const dim = size != null ? { width: size, height: size } : {};
  return (
    <svg
      {...dim}
      viewBox="0 0 100 100"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
      className={className}
    >
      <circle cx="50" cy="50" r="40" fill="var(--color-accent-soft)" />
      <circle
        cx="50"
        cy="50"
        r="40"
        stroke="var(--color-brand-deep)"
        strokeWidth="3.5"
        fill="none"
      />
      <ellipse
        cx="50"
        cy="50"
        rx="40"
        ry="14"
        stroke="var(--color-brand-line)"
        strokeWidth="2.5"
      />
      <ellipse
        cx="50"
        cy="50"
        rx="14"
        ry="40"
        stroke="var(--color-brand-line)"
        strokeWidth="2.5"
      />
      <path
        d="M50 10 C68 22, 68 78, 50 90 C32 78, 32 22, 50 10Z"
        stroke="var(--color-brand-node)"
        strokeWidth="2.5"
      />
      <path
        d="M50 10 C32 22, 32 78, 50 90"
        stroke="var(--color-brand-pale)"
        strokeWidth="2"
        opacity="0.85"
      />
      <path
        d="M10 50 C22 32, 78 32, 90 50 C78 68, 22 68, 10 50Z"
        stroke="var(--color-brand-node)"
        strokeWidth="2"
        opacity="0.55"
      />
      <circle cx="62" cy="38" r="5" fill="var(--color-brand-spark)" />
      <path
        d="M62 43 L62 52"
        stroke="var(--color-brand-spark)"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

interface Props {
  size?: number;
  variant?: 'icon' | 'full';
  compact?: boolean;
  className?: string;
  /** Hide from assistive tech when brand is already announced elsewhere. */
  decorative?: boolean;
}

const BRAND_LABEL = 'Clicks Tourism 360';

export function ProductLogo({
  size,
  variant = 'full',
  compact = false,
  className = '',
  decorative = false,
}: Props) {
  const iconMarkSize = size ?? (compact ? 36 : 44);
  const a11yProps = decorative ? { 'aria-hidden': true as const } : { 'aria-label': BRAND_LABEL };

  if (variant === 'icon') {
    return (
      <div className={`brand-lockup ${styles.lockup} ${className}`} dir="ltr" {...a11yProps}>
        <GlobeMark size={iconMarkSize} />
      </div>
    );
  }

  const lockupClass =
    `brand-lockup ${styles.lockup} ${compact ? styles.compactLockup : ''} ${className}`.trim();

  return (
    <div className={lockupClass} dir="ltr" {...a11yProps}>
      <GlobeMark className={styles.mark} />
      <span className={`brand-clicks ${styles.clicks}`}>Clicks</span>
      <span className={`brand-name ${styles.name}`}>Tourism</span>
      <span className={`brand-badge ${styles.badge}`}>360</span>
    </div>
  );
}
